import ProjectMetadata, { UserReference } from "../models/ProjectMetadata";
import Logger from "../Logger";
import axios from "../utils/axios";
import ServerDataBaseHandler from "../persistence/SeverDataBaseHandler";

const logger = new Logger();
const invitationQueues = new Map<string, Promise<void>>();
// A failed metadata write can be retried without sending the invitation email again.
const unpersistedInvitations = new Map<string, Set<string>>();

export type InviteUserResult = {
  status: "SUCCESS" | "ERROR";
  message: string;
  code?: "INVALID_EMAIL" | "ALREADY_USER" | "INVITE_FAILED" | "METADATA_SAVE_FAILED" | "EXCEPTION";
};

export function validateInviteEmail(emailName: string) {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}$/;
  return emailRegex.test(emailName);
}

type InviteUserParams = {
  project: ProjectMetadata;
  email: string;
  currentUsers?: Array<UserReference>;
};

/** Both invitation UIs must persist the same project, even when another project is open. */
export async function inviteOneUser(params: InviteUserParams): Promise<InviteUserResult> {
  const projectId = params.project.id;
  const project = state.projectMetadata?.id === projectId ? state.projectMetadata : params.project;
  const originalMetadata = project.toJSON();
  const previous = invitationQueues.get(projectId) ?? Promise.resolve();
  const operation = previous.then(() => inviteAndSaveProjectUser(params, projectId, originalMetadata));
  const settled = operation.then(() => {}, () => {});
  invitationQueues.set(projectId, settled);
  try {
    return await operation;
  } finally {
    if (invitationQueues.get(projectId) === settled) invitationQueues.delete(projectId);
  }
}

async function inviteAndSaveProjectUser(params: InviteUserParams, projectId: string, originalMetadata: any): Promise<InviteUserResult> {
  const project = state.projectMetadata?.id === projectId ? state.projectMetadata
    : params.project.id === projectId ? params.project : undefined;
  const email = params.email.trim().toLowerCase();
  const currentUsers: UserReference[] = project?.users || originalMetadata.users || params.currentUsers || [];
  const pending = unpersistedInvitations.get(projectId) ?? new Set<string>();

  if (!validateInviteEmail(email)) {
    return {
      status: "ERROR",
      code: "INVALID_EMAIL",
      message: state.translate("inviteUserDialog.pleaseCorrectEmail", "Bitte gib eine korrekte Email ein"),
    };
  }

  const emailAlreadyUser = currentUsers.some((user) => email === user.email?.trim().toLowerCase());
  if (emailAlreadyUser && !pending.has(email)) {
    return {
      status: "ERROR",
      code: "ALREADY_USER", // no itx
      message: state.translate(
        "inviteUserDialog.personAlreadyExistingText",
        "Bitte Laden Sie eine noch nicht teilhabende Person ein",
      ),
    };
  }

  try {
    const payload = {
      emailOfFriend: email.toLowerCase(),
      projectId,
    };

    if (!pending.has(email)) {
      const res = await axios.post("/api/project/invite/user", payload);
      if (res.data.STATUS !== "SUCCESS") {
        return {
          status: "ERROR",
          code: "INVITE_FAILED",
          message: res.data.MESSAGE || state.translate("inviteUserDialog.inviteSendNotPossible", "Die Einladung konnte nicht versendet werden"),
        };
      }
      pending.add(email);
      unpersistedInvitations.set(projectId, pending);
    }

    const server = new ServerDataBaseHandler();
    const serverMetadata = await server.loadProjectMetadata(projectId);
    // Do not overwrite memberships from a stale open tab or backup if refreshing failed.
    if (!serverMetadata || !Array.isArray(serverMetadata.users)) return metadataSaveFailed();
    const users = serverMetadata.users.map((user: any) => UserReference.fromUser(user));
    for (const invitedEmail of pending) {
      if (!users.some(user => user.email?.trim().toLowerCase() === invitedEmail)) {
        users.push(UserReference.fromUser({ email: invitedEmail }));
      }
    }
    const projects = [...new Set([project, params.project, state.projectMetadata, state.allProjectsMap.get(projectId)])]
      .filter((candidate): candidate is ProjectMetadata => candidate?.id === projectId);
    for (const candidate of projects) {
      candidate.users = users;
      candidate.projectUsersRefreshRequired = false;
    }
    const currentProject = projects[0];
    if (currentProject) state.allProjectsMap.set(projectId, currentProject);
    const metadata = currentProject?.toJSON() ?? originalMetadata;
    metadata.users = users;
    metadata.projectUsersRefreshRequired = false;

    const saved = await server.saveProjectMetadata(projectId, metadata);
    if (saved) {
      unpersistedInvitations.delete(projectId);
      return {
        status: "SUCCESS", // no itx
        message: state.translate(
          "inviteUserDialog.youinvitedT1InProjectT2",
          `Du hast ${email} in das Projekt ${originalMetadata.name} eingeladen`,
          {
            T1: email,
            T2: originalMetadata.name,
          },
        ),
      };
    }

    return metadataSaveFailed();
  } catch (error) {
    logger.log("", error);
    if (pending.has(email)) return metadataSaveFailed();
    return {
      status: "ERROR",
      code: "EXCEPTION", // no itx
      message: state.translate("setPassword.somthingWentwrong", "Something Went wrong."),
    };
  }
}

function metadataSaveFailed(): InviteUserResult {
  return {
    status: "ERROR",
    code: "METADATA_SAVE_FAILED",
    message: state.translate("inviteUserDialog.metadataSaveFailed",
      "Die Einladung wurde versendet, aber die Projektmitarbeiter konnten nicht auf dem Server gespeichert werden. Bitte versuche es erneut; die Einladung wird dabei nicht erneut versendet."),
  };
}
